import { FC } from "react";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ToggleButton } from "@app/shared/toggle-button/ToggleButton";
import { JobPerformanceDashboardRequest } from "@app/operations/job-performance/jobPerformanceInterfaces";

export type ProjectType = 'County' | 'Zip';

export type DateRangeKey = '4h' | '24h' | '7d' | '30d';

export const DATE_RANGE_OPTIONS: { key: DateRangeKey; label: string }[] = [
  { key: '4h',  label: 'Last 4 Hours' },
  { key: '24h', label: 'Last 24 Hours' },
  { key: '7d',  label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
];

const JOB_TYPE_OPTIONS = [
  { id: 1001, name: 'CustomAdequacyProject' },
  { id: 1002, name: 'CustomAdequacyRegionSpecialtyGroupAccess' },
  { id: 1007, name: 'CustomNetworkSummariesProject' },
  { id: 1009, name: 'Export' },
  { id: 1014, name: 'ProcessProviderChanges' },
  { id: 1041, name: 'PciDashboardTablePublish' },
  { id: 1044, name: 'ProjectPublishBackgroundJob' },
  { id: 1102, name: 'ServicingProviderSnowflakeIngestion' },
];

function computeDateRange(key: DateRangeKey): { dateStart: string; dateEnd: string } {
  const now = new Date();
  const msMap = {
    '4h':  4 * 60 * 60 * 1000,
    '7d':  7 * 24 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };
  return {
    dateEnd: now.toISOString(),
    dateStart: new Date(now.getTime() - msMap[key]).toISOString(),
  };
}

interface JobPerformanceFiltersProps {
  dateRangeKey: DateRangeKey;
  filters: JobPerformanceDashboardRequest;
  clients: { clientId: number; name: string }[];
  loading?: boolean;
  projectType: ProjectType;
  onDateRangeKeyChange: (key: DateRangeKey) => void;
  onFiltersChange: (filters: JobPerformanceDashboardRequest) => void;
  onProjectTypeChange: (projectType: ProjectType) => void;
  onClear: () => void;
  onSearch: () => void;
}

const JobPerformanceFilters: FC<JobPerformanceFiltersProps> = ({
  dateRangeKey,
  filters,
  clients,
  loading = false,
  projectType,
  onDateRangeKeyChange,
  onFiltersChange,
  onProjectTypeChange,
  onClear,
  onSearch,
}) => {
  const handleDateRangeChange = (key: DateRangeKey) => {
    onDateRangeKeyChange(key);
    const { dateStart, dateEnd } = computeDateRange(key);
    onFiltersChange({ ...filters, dateEnd, dateStart });
  };

  const handleClientChange = (value: string) => {
    onFiltersChange({
      ...filters,
      clientId: value ? parseInt(value) : undefined,
    });
  };

  const handleJobTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      jobTypeId: value ? parseInt(value) : undefined,
    });
  };

  const handleProjectTypeChange = (key: string) => {
    onProjectTypeChange(key as ProjectType);
  };

  return (
    <div className="d-flex flex-wrap align-items-end gap-3 mb-4 job-performance__filters">
      <div className="qes-select__container job-performance__project-type-field">
        <label className="qes-select__label">Project Type</label>
        <ToggleButton
          buttons={{ County: 'County', Zip: 'Zip' }}
          groupLabel="Project Type"
          onClick={handleProjectTypeChange}
          selectedButton={projectType}
          testId="job-performance__project-type-toggle"
        />
      </div>

      <QESSelect
        label="Date Range"
        testId="job-performance__date-range-select"
        value={dateRangeKey}
        onChange={(e) => handleDateRangeChange(e.target.value as DateRangeKey)}
      >
        {DATE_RANGE_OPTIONS.map(opt => (
          <option key={opt.key} value={opt.key}>{opt.label}</option>
        ))}
      </QESSelect>

      <QESSelect
        label="Client"
        testId="job-performance__client-select"
        value={filters.clientId?.toString() || ''}
        onChange={(e) => handleClientChange(e.target.value)}
      >
        <option value="">All Clients</option>
        {clients.map(c => (
          <option key={c.clientId} value={c.clientId}>{c.name}</option>
        ))}
      </QESSelect>

      {projectType === 'County' && (
        <QESSelect
          label="Job Type"
          testId="job-performance__job-type-select"
          value={filters.jobTypeId?.toString() || ''}
          onChange={(e) => handleJobTypeChange(e.target.value)}
        >
          <option value="">All Job Types</option>
          {JOB_TYPE_OPTIONS.map(jt => (
            <option key={jt.id} value={jt.id}>{jt.id} — {jt.name}</option>
          ))}
        </QESSelect>
      )}

      <div className="d-flex gap-2 job-performance__filter-buttons">
        <QESButton
          disabled={loading}
          qesType="primary"
          qesTestId="job-performance__search-button"
          onClick={onSearch}
        >
          Search
        </QESButton>
        <QESButton
          qesType="secondary"
          qesTestId="job-performance__clear-button"
          onClick={onClear}
        >
          Clear
        </QESButton>
      </div>
      <div className="job-performance__create-report-wrapper" style={{ marginLeft: 'auto' }}>
        <QESButton
          disabled
          qesType="primary"
          qesTestId="job-performance__create-report-button"
        >
          Create Report
        </QESButton>
      </div>
    </div>
  );
};

export default JobPerformanceFilters;
